import type { ReactNode } from 'react'
import { createContext, useState } from 'react'

import { Step } from '../utils/steps'

/**
 * Type definition for the step context value containing step state and setter.
 * Used by the step context provider and consumer hook.
 */
export interface StepContextValue {
  /** The current step in the superhuman-go-intro page flow */
  step: Step
  /** Function to update the current step */
  setStep: (step: Step) => void
}

/**
 * React context for sharing step state across the superhuman-go-intro page component tree.
 * Provides step state and setter function to all child components that consume the context.
 *
 * @default undefined - Context has no default value, must be used within StepProvider
 */
export const StepContext = createContext<StepContextValue | undefined>(undefined)

/**
 * Props interface for the StepProvider component.
 * Defines the configuration options for initializing the step context.
 */
export interface StepProviderProps {
  /** React children components that will have access to the step context */
  children: ReactNode
  /** Optional initial step to start with. Defaults to Step.GrammarlyOnly if not provided */
  initialStep?: Step
}

/**
 * React context provider for managing step state throughout the superhuman-go-intro page.
 * Provides centralized step state that can be consumed by multiple hooks and components.
 *
 * @param props - The props object containing children and optional initialStep
 * @returns JSX provider component wrapping children with step context
 */
export const StepProvider = ({ children, initialStep = Step.GrammarlyOnly }: StepProviderProps) => {
  const [step, setStep] = useState<Step>(initialStep)

  return <StepContext.Provider value={{ step, setStep }}>{children}</StepContext.Provider>
}
